/**
 * API transport.
 *
 * Always same-origin: `/api/v1/...`. In dev, Vite proxies to the API; in production the
 * Cloudflare Pages Function at functions/api/[[path]].ts does. That is what keeps the
 * `platform_session` cookie first-party on *.pages.dev (see vite.config.ts).
 *
 * Ported from the previous implementation's api.ts, with two changes:
 *   - the base is relative rather than a cross-origin VITE_API_URL
 *   - the base is relative rather than a cross-origin VITE_API_URL
 *
 * A vendor's tenant is NOT carried by this client. It is assigned to the session at login, resolved
 * from the email, and read from the session on every later request (apps/api/src/auth.ts RequestGuard)
 * -- so it is neither a form field a vendor has to know nor a header a client could get wrong.
 * `tenantId` below is the operator's case: cross-tenant reads that name the tenant they are for.
 */
import type { Problem } from '@platform/contracts';

export const API_BASE = '/api/v1';

export type Realm = 'vendor' | 'admin';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public traceId?: string,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ---------------------------------------------------------------- session context */

/**
 * The realm is a property of the route, not of stored state: /admin/* is the operator console,
 * everything else is the vendor portal.
 *
 * This was previously read from localStorage, which meant a fresh browser deep-linking into
 * /admin issued vendor-realm requests until someone had signed in through the admin login.
 */
export function getRealm(): Realm {
  if (typeof window === 'undefined') return 'vendor';
  return window.location.pathname.startsWith('/admin') ? 'admin' : 'vendor';
}

/* ---------------------------------------------------------------- request */

export interface ApiOptions extends RequestInit {
  /** Override the realm for this call. Defaults to the stored realm. */
  realm?: Realm;
  /**
   * Scope this request to a tenant by id. Operators must send this for tenant-scoped rows -
   * the API resolves the tenant from x-tenant-id in the admin realm (auth.ts:51) and answers
   * 404 without it.
   */
  tenantId?: string;
}

/**
 * `path` is realm-relative: "/datasets" resolves to /api/v1/datasets for a vendor and
 * /api/v1/admin/datasets for an operator.
 */
export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { realm = getRealm(), tenantId, ...init } = options;
  const url = `${API_BASE}${realm === 'admin' ? '/admin' : ''}${path}`;

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
      ...init.headers
    }
  });

  const body = (await response.json().catch(() => ({}))) as Partial<Problem> & Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      body.code || 'request_failed',
      body.detail || 'Request failed.',
      body.traceId,
      body.errors
    );
  }
  return body as T;
}
